import { useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ContentPlacement } from '@candu-ai/placement/types';
import { PlacementSteps } from 'craft/containers/Toolbox/tabs/Placements/components/ui';
import { FormState } from 'craft/containers/Toolbox/tabs/Placements/constants';
import { setForcePlacementsTab, setToolboxTab, useToolboxTab } from 'craft/state';
import { ToolboxTabs } from 'craft/types';

import { StepOne, StepTwo } from './components';

interface ModalPlacementsProps {
  placement: ContentPlacement;
  placementExists: boolean;
  onEditUrl: VoidFunction;
  placementSaved: {
    values: ContentPlacement;
    id?: string;
  } | null;
  resetPlacementSaved: VoidFunction;
}

export const ModalPlacements = ({
  placement,
  placementExists,
  onEditUrl,
  placementSaved,
  resetPlacementSaved,
}: ModalPlacementsProps) => {
  const dispatch = useDispatch();
  const { forcePlacementsTab } = useToolboxTab();

  const [activeStep, setActiveStep] = useState(placementExists ? undefined : 2);
  const { submitForm, resetForm } = useFormikContext<ContentPlacement>();

  useEffect(() => {
    // make sure that the editor is locked when editing a placement, but not when previewing it or if there is no snapshot
    if (typeof activeStep === 'undefined' && forcePlacementsTab) {
      dispatch(setForcePlacementsTab(false));
    } else if (typeof activeStep !== 'undefined' && !forcePlacementsTab) {
      dispatch(setForcePlacementsTab(true));
    }
  }, [activeStep, forcePlacementsTab]);

  useEffect(() => {
    // used to sync placement state between tab and edit placement modal
    if (placementSaved !== null) {
      setActiveStep(undefined);
      // we need to also reset this form with the modal's placement values
      resetForm(placementSaved);
      resetPlacementSaved();
    }
  }, [placementSaved, resetPlacementSaved]);

  return (
    <PlacementSteps>
      <StepOne active={activeStep === 1} onEdit={onEditUrl} />
      <StepTwo
        state={placementExists ? FormState.UPDATE : FormState.CREATE}
        active={activeStep === 2}
        onSave={() => {
          submitForm().then(() => {
            setActiveStep(undefined);
          });
        }}
        onEdit={() => {
          setActiveStep(2);
        }}
        onCancel={() => {
          setActiveStep(placementExists ? undefined : 2);
          resetForm({ values: placement });
          if (!placementExists) {
            // when first creating a placement, switch to Component tab on cancel and stop forcing placement tab
            dispatch(setToolboxTab(ToolboxTabs.Component));
            dispatch(setForcePlacementsTab(false));
          }
        }}
      />
    </PlacementSteps>
  );
};
